import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, Eye, FileWarning, RefreshCw } from 'lucide-react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { fetchCheckRuns, type CheckRunListDto } from '../services/api'
import { fetchComplianceRunTargets, isActiveRunStatus } from '../services/complianceV2'
import RunProgressStepper from '../components/RunProgressStepper'
import { EmptyState, FilterPill, MimCard, MimPage, StatusPill, formatDateTime } from '../components/MimUi'

const statusFilters = ['All Checks', 'Pass', 'Exceptions', 'Critical']

export default function CheckRunsList() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const focusedId = searchParams.get('checkRunId')
  const [status, setStatus] = useState('All Checks')

  const { data: runs = [], isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['check-runs', 'history'],
    queryFn: () => fetchCheckRuns({ take: 100 }),
    refetchInterval: (query) => {
      const items = query.state.data ?? []
      return items.some((run) => isActiveRunStatus(run.status)) ? 2500 : 10000
    },
  })

  useEffect(() => {
    if (focusedId) navigate(`/check-runs/${focusedId}`, { replace: true })
  }, [focusedId, navigate])

  const filtered = useMemo(() => {
    return runs.filter((run) => {
      if (status === 'Pass') return run.status === 'Completed' && run.findingCount === 0
      if (status === 'Exceptions') return run.findingCount > 0
      if (status === 'Critical') return run.status === 'Failed' || run.findingCount >= 5
      return true
    })
  }, [runs, status])

  const trendData = useMemo(() => {
    return runs.slice(0, 8).reverse().map((run, index) => ({
      label: `Run ${index + 1}`,
      critical: run.status === 'Failed' ? 1 : Math.max(0, Math.min(run.findingCount || 0, 5)),
      warnings: Math.max(0, (run.findingCount || 0) - 5),
      passed: Math.max(0, (run.processedCount || run.targetCount || 0) - (run.findingCount || 0)),
    }))
  }, [runs])

  return (
    <MimPage title="Check History" subtitle="Review past compliance checks and track corrections">
      <div className="mim-stack">
        <MimCard title="Compliance Trend" subtitle="Last completed and active checks">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9edf2" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="critical" stroke="var(--mim-danger)" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="warnings" stroke="var(--mim-warning)" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="passed" stroke="var(--mim-success)" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </MimCard>

        <div className="mim-toolbar">
          {statusFilters.map((filter) => (
            <FilterPill key={filter} active={status === filter} onClick={() => setStatus(filter)}>
              {filter}
            </FilterPill>
          ))}
          <span className="history-count">{filtered.length} checks</span>
        </div>

        <MimCard>
          {isLoading ? (
            <EmptyState title="Loading check history" />
          ) : isError ? (
            <div className="mim-stack" style={{ alignItems: 'center', padding: 24, textAlign: 'center' }}>
              <AlertCircle size={34} color="var(--mim-danger)" />
              <div>
                <strong>Check history could not be loaded</strong>
                <div className="mim-help-text">{getHistoryError(error)}</div>
              </div>
              <button type="button" className="mim-btn mim-btn-secondary" disabled={isFetching} onClick={() => refetch()}>
                <RefreshCw size={15} /> {isFetching ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState title="No checks found" />
          ) : (
            <div className="mim-table-wrap">
              <table className="mim-table">
                <thead>
                  <tr>
                    <th>Report ID</th>
                    <th>Website</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Targets</th>
                    <th>Processed</th>
                    <th>Findings</th>
                    <th>Schedule</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((run) => (
                    <CheckRunHistoryRow key={run.id} run={run} focused={focusedId === run.id} />
                  ))}
                </tbody>
              </table>
              <div className="mim-table-footer">Showing {filtered.length} of {runs.length} checks</div>
            </div>
          )}
        </MimCard>
      </div>
    </MimPage>
  )
}

function CheckRunHistoryRow({ run, focused }: { run: CheckRunListDto; focused: boolean }) {
  const isV2 = run.runType === 'AuditableComplianceV2'
  const active = isActiveRunStatus(run.status)
  const { data: targets = [] } = useQuery({
    queryKey: ['check-run-list-targets', run.id],
    queryFn: () => fetchComplianceRunTargets(run.id),
    enabled: isV2 && active,
    refetchInterval: active ? 2500 : false,
  })

  return (
    <tr className={focused ? 'history-focused-row' : ''}>
      <td className="mono"><Link to={`/check-runs/${run.id}`}>RPT-{run.id.slice(0, 8)}</Link></td>
      <td>
        <strong>{run.rootUrl || run.reason || 'Website compliance check'}</strong>
        {run.errorMessage && <div className="history-error">{run.errorMessage}</div>}
        {active && (
          <div className="history-run-progress">
            <RunProgressStepper
              compact
              status={run.status}
              startedUtc={run.startedUtc}
              completedUtc={run.completedUtc}
              targetStages={targets.map((target) => target.stage)}
            />
          </div>
        )}
      </td>
      <td><StatusPill status={runStatusLabel(run)} /></td>
      <td>{formatDateTime(run.startedUtc)}</td>
      <td>{run.targetCount}</td>
      <td>{run.processedCount}</td>
      <td>{run.findingCount}</td>
      <td>{run.runType === 'Scheduled' ? 'Scheduled' : '-'}</td>
      <td>
        <div className="mim-toolbar">
          <Link className="mim-icon-button" to={`/check-runs/${run.id}`} title="View job progress and details">
            <Eye size={15} />
          </Link>
          <Link className="mim-icon-button" to={`/exception-report?checkRunId=${run.id}`} title="View exception report">
            <FileWarning size={15} />
          </Link>
          <Link className="mim-icon-button" to={run.distributorId ? `/compliance-check?distributorId=${run.distributorId}` : '/compliance-check'} title="Re-run">
            <RefreshCw size={15} />
          </Link>
        </div>
      </td>
    </tr>
  )
}

function getHistoryError(error: unknown) {
  const data = (error as { response?: { data?: unknown } })?.response?.data
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data === 'object' && 'message' in data && typeof (data as { message?: unknown }).message === 'string') {
    return (data as { message: string }).message
  }
  return error instanceof Error ? error.message : 'The server did not return the check-run list.'
}

function runStatusLabel(run: CheckRunListDto) {
  if (run.status === 'Failed') return 'Critical'
  if (run.status === 'Completed' && run.findingCount === 0) return 'Pass'
  if (run.findingCount > 0) return 'Exceptions'
  return run.status
}
